import { differenceInMonths } from 'date-fns'
import { Control, FormState, UseFormSetValue } from 'react-hook-form'
import { ScrollView, View } from 'react-native'

import FormFieldDate from '@/components/form/FormFieldDate'
import FormFieldText from '@/components/form/FormFieldText'
import FosterMedicalForm, {
  fosterMedicalFormStylesheet,
} from '@/components/fosters/cards/FosterMedicalForm'
import FosterMedications from '@/components/fosters/cards/medications/FosterMedications'
import FosterVaccinations from '@/components/fosters/cards/vaccines/FosterVaccinations'
import { FosterForm } from '@/components/fosters/fosterFormSchema'
import Card from '@/components/shared/Card'
import TourSpot from '@/components/tour/TourSpot'
import Species from '@/enums/species'
import useAppTour from '@/hooks/useAppTour'
import FosterService from '@/services/fosterService'
import { AppTourKeys } from '../../../../constants'

interface Props {
  control: Control<FosterForm, any>
  disabled: boolean
  formState: FormState<FosterForm>
  scrollRef: React.RefObject<ScrollView | null>
  setValue: UseFormSetValue<FosterForm>
  fosterId: string
}

const FosterHealthCard = ({
  control,
  disabled,
  formState,
  scrollRef,
  setValue,
  fosterId,
}: Props) => {
  const { foster, isLoading } = FosterService.useFoster(fosterId)

  const showHeartwormTestFields =
    foster?.birthDate &&
    foster?.birthDate instanceof Date &&
    differenceInMonths(new Date(), foster?.birthDate) >= 6

  useAppTour({
    enabled: true,
    tourKey: AppTourKeys.FosterHealthTab,
  })

  return (
    <View style={{ gap: 16 }}>
      <View>
        <FosterMedicalForm
          control={control}
          disabled={disabled}
          formState={formState}
          isLoading={isLoading}
          scrollRef={scrollRef}
          setValue={setValue}
          showHeartwormTestFields={showHeartwormTestFields ?? false}
          species={foster?.species as Species | undefined}
        >
          <TourSpot
            id="foster-health-tab"
            scrollRef={scrollRef}
            style={fosterMedicalFormStylesheet.medicalInfoInput}
          >
            <FormFieldText
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName="microchipId"
              inputMode="numeric"
              isLoading={isLoading}
              label="🏷️ Microchip ID"
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
          </TourSpot>
          {foster?.deceasedDate && (
            <FormFieldDate
              control={control}
              disabled={disabled}
              errors={formState.errors}
              fieldName={'deceasedDate'}
              isLoading={isLoading}
              label="🪦 Date of Death"
              required={false}
              style={fosterMedicalFormStylesheet.medicalInfoInput}
            />
          )}
        </FosterMedicalForm>
      </View>
      <View>
        <TourSpot id="foster-health-medications" scrollRef={scrollRef}>
          <Card title="Medications/Preventions">
            <FosterMedications
              control={control}
              disabled={disabled}
              formState={formState}
              fosterId={fosterId}
            />
          </Card>
        </TourSpot>
      </View>
      <View>
        <TourSpot id="foster-health-vaccinations" scrollRef={scrollRef}>
          <Card title="Vaccinations">
            <FosterVaccinations
              control={control}
              disabled={disabled}
              formState={formState}
              fosterId={fosterId}
            />
          </Card>
        </TourSpot>
      </View>
    </View>
  )
}

export default FosterHealthCard
